import { useSortable } from '@dnd-kit/react/sortable';
import { TreeList } from '@ibsys/kit';
import { GripVerticalIcon } from 'lucide-react';
import type { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { applyNbspTypography, cn } from '@/shared/lib';
import type { NavigationNode } from '../../model';
import { NavigationTreeNode } from './NavigationTreeNode';

type Props = {
  /** Верхнеуровневый узел иерархии атрибутов. */
  node: NavigationNode;
  /** Позиция узла среди верхнеуровневых — от неё считается перестановка. */
  index: number;
  onNodeSelect?: (nodeId: string) => void;
  selectedNodeId?: string | null;
  /** Путь от корня до активного узла — для мемоизации дочерних узлов. */
  selectedPathSet: Set<string>;
};

/**
 * Верхнеуровневый узел меню навигации по атрибутам: перетаскиванием за ручку
 * пользователь задаёт порядок блоков атрибутов. Порядок общий для меню и «портянки»
 * значений, поэтому перетаскивать достаточно в одном месте — в меню блоки видны сразу
 * и коротки, в отличие от значений.
 */
export const SortableTopLevelNode: FC<Props> = ({ node, index, onNodeSelect, selectedNodeId, selectedPathSet }) => {
  const { t } = useTranslation(['dialogs']);
  const { ref, handleRef, isDragging } = useSortable({ id: node.id, index });

  const hasChildren = !!node.children?.length;
  const isSelected = selectedNodeId === node.id;
  const handleSelect = () => onNodeSelect?.(node.id);

  const dragHandle = (
    <div
      ref={(el) => handleRef(el)}
      role="button"
      tabIndex={-1}
      aria-label={t('dialogs:reorderAttributeBlock')}
      className="shrink-0 cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover/top-node:opacity-100"
      // Ручка живёт внутри заголовка: без остановки всплытия перетаскивание сворачивало бы узел
      onClick={(event: MouseEvent) => event.stopPropagation()}
    >
      <GripVerticalIcon className="size-3.5" />
    </div>
  );

  const label = <span className={isSelected ? 'font-bold' : undefined}>{applyNbspTypography(node.name)}</span>;

  if (hasChildren) {
    return (
      // Обёртка обязательна: TreeList.Group рендерит AccordionItem, ref сортировки нужен снаружи
      <div ref={ref} className={cn(isDragging && 'opacity-50')}>
        <TreeList.Group
          value={node.id}
          // pr-2 — ручка не прижимается вплотную к правому краю подсветки узла
          classNames={{ header: 'group/top-node pr-2' }}
          label={label}
          onClick={handleSelect}
          rightSlot={dragHandle}
        >
          {node.children!.map((child) => (
            <NavigationTreeNode
              key={child.id}
              node={child}
              onNodeSelect={onNodeSelect}
              selectedNodeId={selectedNodeId}
              selectedPathSet={selectedPathSet}
            />
          ))}
        </TreeList.Group>
      </div>
    );
  }

  return (
    <div ref={ref} className={cn(isDragging && 'opacity-50')}>
      {/* items-center: узел кита выравнивает содержимое по верху, из-за чего ручка уезжала выше середины строки */}
      <TreeList.Item value={node.id} className="group/top-node items-center" onClick={handleSelect}>
        <span className="flex-1">{label}</span>
        {dragHandle}
      </TreeList.Item>
    </div>
  );
};
